/**
 * Wallet API functions
 * @module lib/api/wallet
 */

import { apiClient } from '@/lib/api/client';
import type { ApiEnvelope } from '@/types/api';
import type {
  WalletBalance,
  Transaction,
  TransactionQueryParams,
  TopUpInput,
  TopUpResponse,
} from '@/types/wallet';

/**
 * Fetch complete wallet balance including all points and financial overview
 *
 * @returns API envelope containing WalletBalance data
 * @example
 * const response = await getWalletBalance();
 * if (response.success) {
 *   console.log('Balance:', response.data.n_c_balance);
 *   console.log('Green Points:', response.data.g_points);
 * }
 */
export async function getWalletBalance(): Promise<ApiEnvelope<WalletBalance>> {
  return apiClient.get<WalletBalance>('/api_partner/v2/wallet/balance');
}

/**
 * Fetch paginated transaction history with optional filters
 *
 * @param params - Optional query parameters for filtering and pagination
 * @returns API envelope containing array of Transaction objects and pagination metadata
 * @example
 * const response = await getTransactions({ page: 1, per_page: 20, status: 'completed' });
 * if (response.success && response.data) {
 *   response.data.forEach(transaction => {
 *     console.log(`Transaction ${transaction.id}: ${transaction.amount}`);
 *   });
 * }
 */
export async function getTransactions(
  params?: TransactionQueryParams
): Promise<ApiEnvelope<Transaction[]>> {
  const searchParams = new URLSearchParams();

  if (params?.page) {
    searchParams.set('page', String(params.page));
  }
  if (params?.per_page) {
    searchParams.set('per_page', String(params.per_page));
  }
  if (params?.type) {
    searchParams.set('type', params.type);
  }
  if (params?.status) {
    searchParams.set('status', params.status);
  }
  if (params?.from_date) {
    searchParams.set('from_date', params.from_date);
  }
  if (params?.to_date) {
    searchParams.set('to_date', params.to_date);
  }

  const queryString = searchParams.toString();
  const path = `/api_partner/v2/wallet/transactions${queryString ? `?${queryString}` : ''}`;

  return apiClient.get<Transaction[]>(path);
}

export async function topUpWallet(input: TopUpInput): Promise<ApiEnvelope<TopUpResponse>> {
  return apiClient.post<TopUpResponse>('/api_partner/v2/wallet/top_up/request', input);
}
